import React, { useState, useEffect } from 'react';
import Modal from '../../../../components/Modal';
import TextField from '../../../../components/TextField';
import Button from '../../../../components/Button';
import Icon from '../../../../components/Icon';
import { 
    FiCalendar, FiMapPin, FiUser, FiInfo, FiLayers, 
    FiCheckSquare, FiDownload, FiClipboard, FiAlertCircle 
} from 'react-icons/fi';
import { useAppSelector } from '../../../../store';
import { updateLeaseRenewalSurvey, type LeaseRenewalSurvey } from '../service/leaseRenewalSurveyService';
import { fetchBranchesData } from '../../../branches/service/branchesService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

type LeaseRenewalSurveyDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    survey: LeaseRenewalSurvey | null;
    mode: 'view' | 'submission';
    onSuccess: (message?: string) => void;
};

export default function LeaseRenewalSurveyDetailsModal({ 
    isOpen, 
    onClose, 
    survey, 
    mode, 
    onSuccess 
}: LeaseRenewalSurveyDetailsModalProps) {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const currentUser = useAppSelector((s) => s.user.currentUser);
    const psoProfile = useAppSelector((s) => s.user.pso);
    const isBankUser = sessionStorage.getItem('role') === 'bank';


    // PSO Submission Fields
    const [renewalPeriod, setRenewalPeriod] = useState<number>(5);
    const [addressOfBranch, setAddressOfBranch] = useState('');
    const [branchType, setBranchType] = useState('Branch');
    const [hasInfrastructure, setHasInfrastructure] = useState(false);
    
    // Infrastructure checklist
    const [infraDetails, setInfraDetails] = useState<Record<string, boolean>>({
        ptclLine: false,
        atm: false,
        locker: false,
        exit: false,
        vantagePoint: false,
        parkingStaff: false,
        parkingCit: false,
        generatorSpace: false,
    });

    const [rccStructured, setRccStructured] = useState(false);
    const [isolatedLocation, setIsolatedLocation] = useState(false);
    const [weakSecurity, setWeakSecurity] = useState(false);
    const [floodArea, setFloodArea] = useState(false);
    const [publicNuisance, setPublicNuisance] = useState(false);
    const [criticalRiskBuilding, setCriticalRiskBuilding] = useState(false);
    const [fuelStationNearby, setFuelStationNearby] = useState(false);
    const [suitableForExtension, setSuitableForExtension] = useState(true);
    const [remarks, setRemarks] = useState('');
    const [surveyDate, setSurveyDate] = useState('');

    useEffect(() => {
        if (isOpen && survey) {
            setErrors({});
            // Set fields from survey
            setRenewalPeriod(
                survey.renewalPeriod 
                    ? parseInt(survey.renewalPeriod.replace(/[^0-9]/g, '')) || 5 
                    : 5
            );
            setAddressOfBranch(survey.addressOfBranch || '');
            setHasInfrastructure(survey.hasInfrastructure || false);
            setInfraDetails(survey.infrastructureDetails || {
                ptclLine: false,
                atm: false,
                locker: false,
                exit: false,
                vantagePoint: false,
                parkingStaff: false,
                parkingCit: false,
                generatorSpace: false,
            });
            setRccStructured(survey.rccStructured || false);
            setIsolatedLocation(survey.isolatedLocation || false);
            setWeakSecurity(survey.weakSecurity || false);
            setFloodArea(survey.floodArea || false);
            setPublicNuisance(survey.publicNuisance || false);
            setCriticalRiskBuilding(survey.criticalRiskBuilding || false);
            setFuelStationNearby(survey.fuelStationNearby || false);
            setSuitableForExtension(survey.suitableForExtension !== false);
            setRemarks(survey.remarks || '');
            setSurveyDate(survey.surveyDate || new Date().toISOString().split('T')[0]);

            // Fetch and set Branch Type dynamically
            resolveBranchType();
        }
    }, [isOpen, survey]);

    const resolveBranchType = async () => {
        if (!survey) return;
        try {
            const uid = currentUser?.uid;
            if (!uid) return;
            const { branches } = await fetchBranchesData(
                uid,
                false,
                psoProfile?.authUid,
                psoProfile?.designation
            );
            const found = branches.find(b => b.branchCode === survey.branchCode);
            if (found && found.branchType) {
                setBranchType(found.branchType);
            } else if (survey.branchType) {
                setBranchType(survey.branchType);
            } else {
                setBranchType('Branch');
            }
        } catch (error) {
            console.error('Error resolving branch type:', error);
            setBranchType(survey?.branchType || 'Branch');
        }
    };

    const handleInfraCheck = (key: string) => {
        setInfraDetails(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handlePdfExport = (item: LeaseRenewalSurvey) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const primaryColor: [number, number, number] = [0, 131, 123];
        const accentColor: [number, number, number] = [0, 131, 123];

        // Header Banner
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.rect(0, 0, 210, 26, 'F');

        // Title (Centered)
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text('LEASE AGREEMENT RENEWAL SURVEY REPORT', 105, 16, { align: 'center' });

        const dateStr = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });

        // Metadata grid
        const metadataRows = [
            [
                { content: 'Survey No:', styles: { fontStyle: 'bold' as const } },
                item.surveyId || 'N/A',
                { content: 'Status:', styles: { fontStyle: 'bold' as const } },
                item.status || 'Assigned'
            ],
            [
                { content: 'Branch Code:', styles: { fontStyle: 'bold' as const } },
                item.branchCode || 'N/A',
                { content: 'Branch Type:', styles: { fontStyle: 'bold' as const } },
                item.branchType || 'Branch'
            ],
            [
                { content: 'Request Date:', styles: { fontStyle: 'bold' as const } },
                item.requestDate || 'N/A',
                { content: 'Assigned PSO:', styles: { fontStyle: 'bold' as const } },
                item.assignedToName || 'N/A'
            ],
            [
                { content: 'Survey Date:', styles: { fontStyle: 'bold' as const } },
                item.surveyDate || 'N/A',
                { content: 'Renewal Period:', styles: { fontStyle: 'bold' as const } },
                item.renewalPeriod || 'N/A'
            ]
        ];

        autoTable(doc, {
            startY: 32,
            theme: 'plain',
            body: metadataRows,
            bodyStyles: { fontSize: 8.5, cellPadding: 1.5 },
            columnStyles: {
                0: { cellWidth: 28 },
                1: { cellWidth: 62 },
                2: { cellWidth: 28 },
                3: { cellWidth: 62 }
            }
        });

        // Address Box
        const inchargeText = `Region: ${item.branchLocation || 'N/A'}\nShort Address: ${item.branchName || 'N/A'}`;
        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 3,
            theme: 'grid',
            head: [['Brand Detail', 'Branch Address']],
            body: [[inchargeText, item.addressOfBranch || 'N/A']],
            headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 8.5, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 90 },
                1: { cellWidth: 90 }
            }
        });

        let currentY = (doc as any).lastAutoTable.finalY + 3;

        // Checklist Facilities (Conditional)
        if (item.hasInfrastructure) {
            const facilitiesList: string[] = [];
            const labelsMap: Record<string, string> = {
                ptclLine: 'PTCL Line',
                atm: 'ATM',
                locker: 'Locker',
                exit: 'Emergency Exit',
                vantagePoint: 'Vantage Point',
                parkingStaff: 'Parking Space for staff',
                parkingCit: 'Parking Space for CIT Van',
                generatorSpace: 'Generator Space',
            };
            if (item.infrastructureDetails) {
                Object.entries(item.infrastructureDetails).forEach(([k, v]) => {
                    if (v) facilitiesList.push(labelsMap[k] || k);
                });
            }
            const facilitiesStr = facilitiesList.length > 0 
                ? facilitiesList.join(', ') 
                : 'No special facilities selected.';

            autoTable(doc, {
                startY: currentY,
                theme: 'grid',
                head: [['INFRASTRUCTURE & FACILITIES']],
                body: [[facilitiesStr]],
                headStyles: { fillColor: accentColor, textColor: [255, 255, 255], fontStyle: 'bold' },
                bodyStyles: { fontSize: 8.5, cellPadding: 3 }
            });
            currentY = (doc as any).lastAutoTable.finalY + 3;
        }

        // Assessment Details Checklist
        const yesNo = (v: boolean | undefined) => v ? 'Yes' : 'No';
        const questionRows = [
            ['RCC Structured (Locker / Strong Room)', yesNo(item.rccStructured)],
            ['Isolated Location', yesNo(item.isolatedLocation)],
            ['Weak or Not Suitable from Security Point of View', yesNo(item.weakSecurity)],
            ['Flood Area', yesNo(item.floodArea)],
            ['Public Nuisance Area', yesNo(item.publicNuisance)],
            ['Critical Risk Building in Area', yesNo(item.criticalRiskBuilding)],
            ['Fuel Station Nearby', yesNo(item.fuelStationNearby)],
            ['Suitable for Extension / Renewal', yesNo(item.suitableForExtension)],
        ];

        autoTable(doc, {
            startY: currentY,
            theme: 'grid',
            head: [['Assessment Factor / Parameter Checked', 'Condition Met / Checked']],
            body: questionRows,
            headStyles: { fillColor: accentColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 140 },
                1: { cellWidth: 40, halign: 'center', fontStyle: 'bold' as const }
            },
            bodyStyles: { fontSize: 8.5, cellPadding: 2 }
        });

        // Professional Remarks Table
        autoTable(doc, {
            startY: (doc as any).lastAutoTable.finalY + 3,
            theme: 'grid',
            head: [['Remarks']],
            body: [[item.remarks || 'No relocation remarks provided.']],
            headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
            bodyStyles: { fontSize: 8.5, cellPadding: 4 }
        });

        // Footer block
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated By: ${item.assignedToName || 'System'}      |      Date: ${dateStr}`, 15, pageHeight - 9);

        const fileName = `Lease_Renewal_Report_${item.surveyId || 'N/A'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;

        const newErrors: Record<string, string> = {};
        if (!renewalPeriod || renewalPeriod < 1) {
            newErrors.renewalPeriod = 'Renewal period must be equal or greater than 1 Year';
        }
        if (!addressOfBranch.trim()) {
            newErrors.addressOfBranch = 'Address of branch is required (must be verified and typed by PSO)';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (!survey) return;

        setLoading(true);
        try {
            const today = new Date().toISOString().split('T')[0];
            const updates = {
                status: 'Submitted' as const,
                renewalPeriod: `${renewalPeriod} Year${renewalPeriod > 1 ? 's' : ''}`,
                addressOfBranch: addressOfBranch.trim(),
                branchType,
                hasInfrastructure,
                infrastructureDetails: infraDetails,
                rccStructured,
                isolatedLocation,
                weakSecurity,
                floodArea,
                publicNuisance,
                criticalRiskBuilding,
                fuelStationNearby,
                suitableForExtension,
                remarks: remarks.trim(),
                surveyDate: surveyDate,
                surveySubmittedOn: today,
            };

            await updateLeaseRenewalSurvey(survey.id, survey.bankId, updates);

            // Auto export PDF upon submission
            const fullSurveyRecord = {
                ...survey,
                ...updates
            };
            handlePdfExport(fullSurveyRecord);

            onSuccess('Lease renewal survey submitted successfully. PDF report has been downloaded.');
            onClose();
        } catch (error: any) {
            console.error('Error submitting lease survey:', error);
            setErrors({ submit: error.message || 'Failed to submit survey' });
        } finally {
            setLoading(false);
        }
    };

    if (!survey) return null;

    const isViewMode = mode === 'view' || survey.status === 'Submitted';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isViewMode ? `Lease Renewal Survey Details — ${survey.surveyId}` : `Lease Renewal Survey Submission — ${survey.surveyId}`}
            size="lg"
            closeOnBackdropClick={false}
            footer={
                <div className="flex justify-end gap-3 px-6 py-4">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {isViewMode ? 'Close' : 'Cancel'}
                    </Button>
                    
                    {isViewMode && (
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => handlePdfExport(survey)}
                            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                        >
                            <Icon icon={FiDownload} />
                            Download PDF Summary
                        </Button>
                    )}

                    {!isViewMode && (
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={loading}
                            form="lease-details-form"
                            className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white"
                        >
                            <Icon icon={FiCheckSquare} />
                            {loading ? 'Submitting...' : 'Submit Survey'}
                        </Button>
                    )}
                </div>
            }
        >
            <form id="lease-details-form" onSubmit={handleSubmit} className="p-6 space-y-6">
                {errors.submit && (
                    <div className="rounded-xl bg-red-50 p-4 text-sm text-red-600 dark:bg-red-950/20 dark:text-red-400">
                        {errors.submit}
                    </div>
                )}

                {/* Section 1: Pre-filled Assignment Parameters */}
                <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/50">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
                        <Icon icon={FiClipboard} className="h-3.5 w-3.5" />
                        Assignment Details (Read Only)
                    </h4>

                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                        <div>
                            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                                Survey ID
                            </span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block mt-0.5">
                                {survey.surveyId}
                            </span>
                        </div>

                        <div>
                            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                                Branch Code
                            </span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block mt-0.5">
                                {survey.branchCode}
                            </span>
                        </div>

                        <div>
                            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                                Request Date
                            </span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block mt-0.5 flex items-center gap-1">
                                <Icon icon={FiCalendar} className="text-blue-500 h-3.5 w-3.5 shrink-0" />
                                {survey.requestDate}
                            </span>
                        </div>

                        <div>
                            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                                Assigned PSO
                            </span>
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 block mt-0.5 flex items-center gap-1">
                                <Icon icon={FiUser} className="text-teal-500 h-3.5 w-3.5 shrink-0" />
                                {survey.assignedToName}
                            </span>
                        </div>

                        <div>
                            <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                                Status
                            </span>
                            {isBankUser ? (
                                <select
                                    value={survey.status || 'Assigned'}
                                    disabled={loading}
                                    onChange={async (e) => {
                                        const newStatus = e.target.value as 'Assigned' | 'Submitted';
                                        if (!window.confirm(`Are you sure you want to change the status of this survey to "${newStatus}"?`)) {
                                            return;
                                        }
                                        setLoading(true);
                                        try {
                                            await updateLeaseRenewalSurvey(survey.id, survey.bankId, { status: newStatus });
                                            onSuccess(`Status updated to ${newStatus} successfully.`);
                                            onClose();
                                        } catch (error: any) {
                                            console.error('Error updating status:', error);
                                            setErrors({ submit: error.message || 'Failed to update status' });
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white py-1 px-2 text-xs font-semibold text-slate-700 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                >
                                    <option value="Assigned">Assigned</option>
                                    <option value="Submitted">Submitted</option>
                                </select>
                            ) : (
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold mt-1 ${
                                    survey.status === 'Submitted'
                                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400'
                                        : 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400'
                                }`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${survey.status === 'Submitted' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                    {survey.status}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="border-t border-slate-200 dark:border-slate-800 mt-3 pt-3">
                        <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 block uppercase tracking-wider">
                            Incharge Branch Address
                        </span>
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200 block mt-0.5">
                            {survey.branchName}
                        </span>
                    </div>
                </div>

                {/* Section 2: Physical Verification Fields */}
                <div className="space-y-6">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800 pb-2 flex items-center gap-1.5">
                        <Icon icon={FiLayers} className="h-3.5 w-3.5 text-blue-500" />
                        Physical Property Assessment
                    </h4>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                        {/* Renewal Period */}
                        <div>
                            <label className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300 block">
                                Renewal Period (Years)
                            </label>
                            <input
                                type="number"
                                min={1}
                                value={renewalPeriod}
                                onChange={(e) => setRenewalPeriod(Math.max(1, parseInt(e.target.value) || 1))}
                                disabled={isViewMode || loading}
                                className={`w-full rounded-xl border bg-white py-2.5 px-3 text-sm font-semibold text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-slate-900 dark:text-white ${
                                    errors.renewalPeriod ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'
                                }`}
                                required
                            />
                            {errors.renewalPeriod && (
                                <p className="mt-1 text-xs text-red-500">{errors.renewalPeriod}</p>
                            )}
                        </div>

                        {/* Branch Type */}
                        <div>
                            <label className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300 block">
                                Branch Type (Auto fetched)
                            </label>
                            <input
                                type="text"
                                value={branchType}
                                disabled
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 px-3 text-sm font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                            />
                        </div>

                        {/* Survey Date */}
                        <div>
                            <label className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300 block">
                                Survey Date
                            </label>
                            <input
                                type="date"
                                value={surveyDate}
                                onChange={(e) => setSurveyDate(e.target.value)}
                                disabled={isViewMode || loading}
                                className={`w-full rounded-xl border bg-white py-2.5 px-3 text-sm font-semibold text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-slate-900 dark:text-white ${
                                    isViewMode ? 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400' : 'border-slate-200 dark:border-slate-700'
                                }`}
                                required
                            />
                        </div>
                    </div>

                    {/* Address of Branch (Filled by PSO) */}
                    <div>
                        <label className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300 block">
                            Address of Branch (Physical validation full address — *Do not auto fill)
                        </label>
                        <textarea
                            rows={2}
                            value={addressOfBranch}
                            onChange={(e) => setAddressOfBranch(e.target.value)}
                            disabled={isViewMode || loading}
                            placeholder="Please type full verified branch address manually..."
                            className={`w-full rounded-xl border bg-white py-2.5 px-3 text-sm font-medium text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-slate-900 dark:text-white ${
                                errors.addressOfBranch ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'
                            }`}
                            required
                        />
                        {errors.addressOfBranch && (
                            <p className="mt-1 text-xs text-red-500">{errors.addressOfBranch}</p>
                        )}
                    </div>

                    {/* Infrastructure & Facilities Toggle & Checklist */}
                    <div className="rounded-xl border border-slate-100 bg-slate-50/30 p-4 dark:border-slate-800 dark:bg-slate-900/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                    Special Infrastructure & Facilities Available?
                                </span>
                                <p className="text-xs text-slate-400">
                                    Toggle ON if the premises contains security facilities or utilities.
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={hasInfrastructure}
                                    onChange={(e) => {
                                        if (isViewMode) return;
                                        setHasInfrastructure(e.target.checked);
                                    }}
                                    disabled={isViewMode || loading}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-300 dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                        </div>

                        {hasInfrastructure && (
                            <div className="grid grid-cols-2 gap-3 mt-4 sm:grid-cols-4 pt-4 border-t border-dashed border-slate-200 dark:border-slate-800">
                                {[
                                    { key: 'ptclLine', label: 'PTCL Line' },
                                    { key: 'atm', label: 'ATM' },
                                    { key: 'locker', label: 'Locker' },
                                    { key: 'exit', label: 'Emergency Exit' },
                                    { key: 'vantagePoint', label: 'Vantage Point' },
                                    { key: 'parkingStaff', label: 'Parking Space (Staff)' },
                                    { key: 'parkingCit', label: 'Parking Space (CIT)' },
                                    { key: 'generatorSpace', label: 'Generator Space' },
                                ].map((item) => (
                                    <label
                                        key={item.key}
                                        className={`flex items-center gap-2 rounded-lg border p-2.5 transition text-xs font-semibold ${
                                            infraDetails[item.key]
                                                ? 'border-blue-200 bg-blue-50/50 text-blue-800 dark:border-blue-900/30 dark:bg-blue-950/20 dark:text-blue-400'
                                                : 'border-slate-100 bg-white text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400'
                                        } ${!isViewMode && !loading ? 'cursor-pointer hover:border-slate-300' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={infraDetails[item.key]}
                                            onChange={() => {
                                                if (isViewMode || loading) return;
                                                handleInfraCheck(item.key);
                                            }}
                                            disabled={isViewMode || loading}
                                            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        {item.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Section 3: Assessment Parameters switches */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {[
                            { state: rccStructured, setter: setRccStructured, label: 'RCC Structured (Locker / Strong Room)', desc: 'Has solid concrete security structures.' },
                            { state: isolatedLocation, setter: setIsolatedLocation, label: 'Isolated Location', desc: 'Is branch situated in a remote or lonely area.' },
                            { state: weakSecurity, setter: setWeakSecurity, label: 'Weak or Not Suitable Security', desc: 'Premises poses significant security hazards.' },
                            { state: floodArea, setter: setFloodArea, label: 'Flood Prone Area', desc: 'Area has a history of high waters or flooding.' },
                            { state: publicNuisance, setter: setPublicNuisance, label: 'Public Nuisance Area', desc: 'Branch surrounded by noisy, hazardous, or crowded markets.' },
                            { state: criticalRiskBuilding, setter: setCriticalRiskBuilding, label: 'Critical Risk Building', desc: 'The building itself has structures at risk of failure.' },
                            { state: fuelStationNearby, setter: setFuelStationNearby, label: 'Fuel Station Nearby', desc: 'Has fuel dispensing or flammable depot nearby.' },
                            { state: suitableForExtension, setter: setSuitableForExtension, label: 'Suitable for Extension / Renewal', desc: 'The site is fit to continue lease renewal.', isPositive: true },
                        ].map((item, idx) => (
                            <div 
                                key={idx} 
                                className={`flex items-center justify-between p-3.5 rounded-xl border transition ${
                                    item.state 
                                        ? (item.isPositive ? 'border-green-100 bg-green-50/30 dark:border-green-950/20 dark:bg-green-950/5' : 'border-red-100 bg-red-50/30 dark:border-red-950/20 dark:bg-red-950/5')
                                        : 'border-slate-100 bg-slate-50/20 dark:border-slate-800 dark:bg-slate-900/20'
                                }`}
                            >
                                <div className="pr-3">
                                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">
                                        {item.label}
                                    </span>
                                    <span className="text-[10px] text-slate-400 block mt-0.5">
                                        {item.desc}
                                    </span>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={item.state}
                                        onChange={(e) => {
                                            if (isViewMode) return;
                                            item.setter(e.target.checked);
                                        }}
                                        disabled={isViewMode || loading}
                                        className="sr-only peer"
                                    />
                                    <div className={`w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:bg-slate-700 ${
                                        item.isPositive ? 'peer-checked:bg-green-600' : 'peer-checked:bg-red-500'
                                    }`}></div>
                                </label>
                            </div>
                        ))}
                    </div>

                    {/* Remarks / Relocation Rationale */}
                    <div>
                        <label className="mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300 block">
                            Detail Rationale for Relocation / Remarks
                        </label>
                        <textarea
                            rows={3}
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            disabled={isViewMode || loading}
                            placeholder="Please provide specific reasoning or comments regarding relocations, structural faults, or extension recommendations..."
                            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 px-3 text-sm font-medium text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        />
                    </div>
                </div>
            </form>
        </Modal>
    );
}
